import { useTranslation } from 'react-i18next';

import type { HourState } from './SiteCharts.js';

/**
 * Availability as the heat maps of the sheet (SP-UX10.1, owner 11.10: 4a). The data is what the
 * API already returns: hourly buckets of GET /sites/:id/uptime?range=7d and the outages from
 * GET /sites/:id/incidents (kind "downtime" only, like hourState).
 */

export interface UptimePoint {
  readonly t: string;
  readonly checks: number;
  readonly failed: number;
  readonly avgResponseMs?: number | null;
}

export interface IncidentSpan {
  readonly kind: string;
  readonly startedAt: string;
  readonly resolvedAt: string | null;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

const formats = new Map<string, Intl.DateTimeFormat>();

/** Date and hour of an instant in a time zone (one formatter per zone, not one per cell). */
function localParts(at: Date, timeZone: string) {
  let format = formats.get(timeZone);
  if (!format)
    formats.set(
      timeZone,
      (format = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hourCycle: 'h23',
      })),
    );
  const parts = format.formatToParts(at);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { day: `${part('year')}-${part('month')}-${part('day')}`, hour: Number(part('hour')) };
}

/** The last `count` calendar days (oldest first) up to `now` in the time zone. Counted on the
 * calendar, not in 24-hour steps, so a change of the clocks neither skips nor repeats a day. */
function lastDays(now: Date, timeZone: string, count: number) {
  const [y = 1970, m = 1, d = 1] = localParts(now, timeZone).day.split('-').map(Number);
  return Array.from({ length: count }, (_, i) =>
    new Date(Date.UTC(y, m - 1, d - (count - 1 - i))).toISOString().slice(0, 10),
  );
}

const outages = (incidents: readonly IncidentSpan[], now: Date) =>
  incidents
    .filter((i) => i.kind === 'downtime')
    .map((i) => [Date.parse(i.startedAt), i.resolvedAt ? Date.parse(i.resolvedAt) : now.getTime()]);

const overlaps = (spans: readonly number[][], from: number, to: number) =>
  spans.some(([start = 0, end = 0]) => start < to && end > from);

const stateOf = (cell: { checks: number; failed: number; down: boolean } | undefined): HourState =>
  !cell
    ? 'none'
    : cell.down
      ? 'down'
      : cell.checks === 0
        ? 'none'
        : cell.failed > 0
          ? 'degraded'
          : 'ok';

export interface HeatRow {
  readonly day: string;
  readonly hours: readonly HourState[];
}

/** The last 7 days (oldest first) × 24 hours in the account's time zone. */
export function weekHeat(
  points: readonly UptimePoint[],
  incidents: readonly IncidentSpan[],
  timeZone: string,
  now: Date,
): HeatRow[] {
  const days = lastDays(now, timeZone, 7);
  const cells = new Map<string, { checks: number; failed: number; down: boolean }>();
  const cell = (at: Date) => {
    const { day, hour } = localParts(at, timeZone);
    const key = `${day}|${hour}`;
    let c = cells.get(key);
    if (!c) cells.set(key, (c = { checks: 0, failed: 0, down: false }));
    return c;
  };
  for (const p of points) {
    const c = cell(new Date(p.t));
    c.checks += p.checks;
    c.failed += p.failed;
  }
  // Every UTC hour of the window that an outage touched (hours, not local midnights, so DST fits).
  const spans = outages(incidents, now);
  const last = Math.floor(now.getTime() / HOUR) * HOUR;
  for (let h = last - 8 * DAY; h <= last; h += HOUR)
    if (overlaps(spans, h, h + HOUR)) cell(new Date(h)).down = true;
  return days.map((day) => ({
    day,
    hours: Array.from({ length: 24 }, (_, hour) => stateOf(cells.get(`${day}|${hour}`))),
  }));
}

const CELL: Readonly<Record<HourState, string>> = { ok: '', degraded: 'w', down: 'b', none: 'n' };

const counts = (states: readonly HourState[]) => ({
  ok: states.filter((s) => s === 'ok').length,
  degraded: states.filter((s) => s === 'degraded').length,
  down: states.filter((s) => s === 'down').length,
  none: states.filter((s) => s === 'none').length,
});

/** 7 × 24: a row per day, a cell per hour (sheet: .heat). */
export function HourHeat({ rows }: { rows: readonly HeatRow[] }) {
  const { t } = useTranslation();
  return (
    <div
      className="heat"
      role="img"
      aria-label={t('heat.weekSr', counts(rows.flatMap((r) => r.hours)))}
    >
      {rows.flatMap((r) =>
        r.hours.map((state, hour) => (
          <i
            key={`${r.day}-${hour}`}
            className={CELL[state]}
            title={`${r.day} ${String(hour).padStart(2, '0')}:00 – ${t(`overview.pulse.${state}`)}`}
          />
        )),
      )}
    </div>
  );
}
